'use client';

import { useCallback, useEffect, useEffectEvent } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { analytics } from '@kit/analytics';
import {
  AppEvent,
  AppEventType,
  ConsumerProvidedEventTypes,
  useAppEvents,
} from '@kit/shared/events';
import { isBrowser } from '@kit/shared/utils';
import type { ProductOpenEvents } from '@kit/tasks/analytics';

import type { PlanningEvents } from '~/lib/events/planning-events';

type AnalyticsMapping<
  T extends ConsumerProvidedEventTypes = NonNullable<unknown>,
> = {
  [K in AppEventType<T>]?: (event: AppEvent<T, K>) => unknown;
};

/**
 * Hook to subscribe to app events and map them to analytics actions
 * @param mapping
 */
function useAnalyticsMapping<T extends ConsumerProvidedEventTypes>(
  mapping: AnalyticsMapping<T>,
) {
  const appEvents = useAppEvents<T>();

  const getMapping = useEffectEvent(() => mapping);

  const subscribeToAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.on(eventType, handler);
    },
  );

  const unsubscribeFromAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.off(eventType, handler);
    },
  );

  useEffect(() => {
    const currentMapping = getMapping();
    const subscriptions = Object.entries(currentMapping).map(
      ([eventType, handler]) => {
        subscribeToAppEvent(eventType as AppEventType<T>, handler);

        return () =>
          unsubscribeFromAppEvent(eventType as AppEventType<T>, handler);
      },
    );

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [mapping]);
}

/**
 * Define a mapping of app events to analytics actions
 * Add new mappings here to track new events in the analytics service from app events
 */
const analyticsMapping: AnalyticsMapping<PlanningEvents> = {
  'user.signedIn': (event) => {
    const { userId, ...traits } = event.payload;

    if (userId) {
      return analytics.identify(userId, traits);
    }
  },
  'user.signedUp': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'user.signedOut': () => {
    // Clear per-tenant super properties so they don't carry into the next
    // user's session on a shared browser.
    return analytics.unregisterProperty('account_slug');
  },
  'checkout.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'user.updated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },

  // Planning events - client-side only
  'planning.document.create_initiated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.template.wizard.step_completed': (event) => {
    return analytics.trackEvent(event.type, {
      ...event.payload,
      step: String(event.payload.step),
    });
  },
  'planning.transcription.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.transcription.stopped': (event) => {
    const { duration_seconds, ...rest } = event.payload;
    return analytics.trackEvent(event.type, {
      ...rest,
      ...(duration_seconds !== undefined && {
        duration_seconds: String(duration_seconds),
      }),
    });
  },
  'planning.section.saved': (event) => {
    return analytics.trackEvent(event.type, {
      ...event.payload,
      content_length: String(event.payload.content_length),
    });
  },
  'planning.ai_review.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.ai_transform.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.ai_transform.completed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.ai_transform.error': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.ai_transform.accepted': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.export.copy': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.export.download': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.transcript.saved': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.vault.synced': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.wizard.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.tutorial.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.tutorial.skipped': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.tutorial.completed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'planning.tutorial.restarted': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
};

/**
 * Workspaces/Playbooks "opened" events. These are navigations with no server
 * mutation, so — unlike the created/applied lifecycle events, which fire
 * server-side — they are emitted client-side (page-view style) and mapped here.
 * Keyed by the `account` group for per-tenant breakdowns.
 */
const productOpenMapping: AnalyticsMapping<ProductOpenEvents> = {
  'workspace.opened': (event) => {
    return analytics.trackEvent(event.type, event.payload, {
      groups: { account: event.payload.account_id },
    });
  },
  'playbook.opened': (event) => {
    return analytics.trackEvent(event.type, event.payload, {
      groups: { account: event.payload.account_id },
    });
  },
};

function AnalyticsProviderBrowser(props: React.PropsWithChildren) {
  // Subscribe to app events and map them to analytics actions
  useAnalyticsMapping<PlanningEvents>(analyticsMapping);
  useAnalyticsMapping<ProductOpenEvents>(productOpenMapping);

  // Report page views to the analytics service
  useReportPageView(useCallback((url) => analytics.trackPageView(url), []));

  // Render children
  return props.children;
}

/**
 * Provider for the analytics service
 */
export function AnalyticsProvider(props: React.PropsWithChildren) {
  if (!isBrowser()) {
    return props.children;
  }

  return <AnalyticsProviderBrowser>{props.children}</AnalyticsProviderBrowser>;
}

/**
 * Hook to report page views to the analytics service
 * @param reportAnalyticsFn
 */
function useReportPageView(reportAnalyticsFn: (url: string) => unknown) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const callAnalyticsOnPathChange = useEffectEvent(() => {
    const url = [pathname, searchParams.toString()].filter(Boolean).join('?');

    return reportAnalyticsFn(url);
  });

  useEffect(() => {
    callAnalyticsOnPathChange();
    // call whenever the pathname changes
  }, [pathname]);
}
